/*!
 * DIREVULF — Fandom pictures for the menu tiles and "Shop by fandom" cards
 * (assets/fandom-tiles.js)
 *
 * A fandom tile in the phone menu, or a "Shop by fandom" card, that has no picture of its own
 * (no poster, hero image or logo on the Fandom entry, no collection image and no picture
 * uploaded on the card's block) is given one automatically: a photo of one of that fandom's
 * characters from the Fandom Hub's own picture list (assets/fandom-hub.json) — the licence-
 * checked Wikimedia Commons photos the fandom page already shows, such as a Spider-Man figure
 * for Marvel or a Toothless figure for How to Train Your Dragon. A tile or card is matched to
 * its fandom by the collection it links to.
 *
 * The picture is hot-linked from Wikimedia Commons and credited in its tooltip (author,
 * licence, Wikimedia Commons). Anything the owner sets later wins: only empty tiles and cards
 * are filled, and a picture that will not load leaves the tile as it was.
 *
 * The picture list is fetched once (a long-cached theme file) when a card is on the page or
 * the menu is first opened, never on a page that shows neither.
 */
(function () {
  'use strict';
  var me = document.currentScript;
  var url = me && me.getAttribute('data-content');
  if (!url || !window.fetch || !document.querySelector) return;

  var COMMONS = 'https://upload.wikimedia.org/wikipedia/commons/';
  var STEPS = [330, 500, 960];
  var data = null;

  function load() {
    if (!data) {
      data = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .catch(function () { data = null; return null; });
    }
    return data;
  }

  function handleOf(href) {
    var m = String(href || '').match(/\/collections\/([^/?#]+)/);
    if (!m) return '';
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }

  /* A Commons file at a standard thumbnail width (the original when it is smaller). */
  function fileUrl(im, w) {
    var name = encodeURIComponent(im.f.replace(/ /g, '_')).replace(/%2C/g, ',');
    if (im.o || (im.w && im.w <= w)) return COMMONS + im.p + '/' + name;
    return COMMONS + 'thumb/' + im.p + '/' + name + '/' + w + 'px-' + name;
  }

  /* The fandom's best photo for a frame of the given shape: a picture of a character first, then
     one already close to the frame's proportions, then the hub's own order. */
  function pick(c, key, lo, hi, avoid) {
    var I = c.images || {};
    var list = Object.keys(I).filter(function (id) {
      var im = I[id];
      return im.fd === key && (im.t || 'photo') === 'photo' && (im.w || 0) >= 900 && im.f && im.p && !avoid[id];
    }).map(function (id, i) {
      var im = I[id], r = im.r || 1;
      return { id: id, i: i, ch: im.ch && im.ch.length ? 0 : 1, fit: r >= lo && r <= hi ? 0 : 1 };
    }).sort(function (a, b) { return a.ch - b.ch || a.fit - b.fit || a.i - b.i; });
    return list.length ? list[0].id : null;
  }

  function fandomFor(c, handle) {
    return (c.fandoms || []).filter(function (f) { return f.collection === handle; })[0] || null;
  }

  function picture(im, id, sizes, cls, style) {
    var img = document.createElement('img');
    var widths = STEPS.filter(function (w) { return !im.o && (!im.w || im.w > w); });
    img.src = fileUrl(im, widths.length ? widths[Math.min(1, widths.length - 1)] : 960);
    if (widths.length > 1) {
      img.srcset = widths.map(function (w) { return fileUrl(im, w) + ' ' + w + 'w'; }).join(', ');
      img.sizes = sizes;
    }
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    if (im.w && im.h) { img.width = im.w; img.height = im.h; }
    if (cls) img.className = cls;
    if (style) img.setAttribute('style', style);
    img.title = 'Photo: ' + (im.a || 'Wikimedia Commons') + (im.l ? ' · ' + im.l : '') + ' · Wikimedia Commons' + (im.cap ? ' — ' + im.cap : '');
    img.setAttribute('data-dv-fandom-pic', id);
    return img;
  }

  var used = {};

  /* Menu tiles: an empty .mobile-nav__tile-img (5:4). */
  function fillTiles(c) {
    Array.prototype.forEach.call(document.querySelectorAll('.mobile-nav__tile'), function (tile) {
      var box = tile.querySelector('.mobile-nav__tile-img');
      if (!box || box.querySelector('img') || box.__dvFilled) return;
      var f = fandomFor(c, handleOf(tile.getAttribute('href')));
      var avoid = {}; if (f && used[f.key + ':card']) avoid[used[f.key + ':card']] = 1;
      var id = f && pick(c, f.key, 1.1, 1.8, avoid);
      if (!id) return;
      box.__dvFilled = true;
      used[f.key + ':tile'] = id;
      var img = picture(c.images[id], id, '120px');
      img.addEventListener('error', function () { if (img.parentNode) img.parentNode.removeChild(img); box.__dvFilled = false; });
      box.appendChild(img);
    });
  }

  /* "Shop by fandom" cards: a .dv-fcard whose media shows only the placeholder (4:5 on the
     homepage). The placeholder is put back if the picture will not load. */
  function fillCards(c) {
    Array.prototype.forEach.call(document.querySelectorAll('.dv-fcard'), function (card) {
      var ph = card.querySelector('.dv-fcard__media > .dv-fcard__placeholder');
      if (!ph) return;
      var f = fandomFor(c, handleOf(card.getAttribute('href')));
      var avoid = {}; if (f && used[f.key + ':tile']) avoid[used[f.key + ':tile']] = 1;
      var id = f && pick(c, f.key, 0.6, 1.0, avoid);
      if (!id) return;
      used[f.key + ':card'] = id;
      var img = picture(c.images[id], id, '(min-width: 990px) 20vw, (min-width: 750px) 33vw, 62vw', 'dv-fcard__autopic',
        'position:absolute;inset:0;width:100%;height:100%;object-fit:cover');
      img.addEventListener('error', function () { if (img.parentNode) img.parentNode.replaceChild(ph, img); });
      ph.parentNode.replaceChild(img, ph);
    });
  }

  function run(what) {
    load().then(function (c) {
      if (!c) return;
      if (what !== 'tiles') fillCards(c);
      if (what !== 'cards') fillTiles(c);
    });
  }

  function emptyCards() { return !!document.querySelector('.dv-fcard .dv-fcard__media > .dv-fcard__placeholder'); }
  function emptyTiles() {
    return Array.prototype.some.call(document.querySelectorAll('.mobile-nav__tile-img'), function (b) { return !b.querySelector('img'); });
  }

  function start() {
    // Cards on this page: fill them now (and the menu with the same download).
    if (emptyCards()) { run('all'); return; }
    // Otherwise wait for the menu: when it opens, or when a finger or pointer heads for its button.
    var nav = document.getElementById('DvMobileNav');
    if (!nav || !emptyTiles()) return;
    var done = false;
    function go() { if (done) return; done = true; run('tiles'); }
    if (nav.hasAttribute('open')) { go(); return; }
    if (window.MutationObserver) {
      new MutationObserver(function () { if (nav.hasAttribute('open')) go(); }).observe(nav, { attributes: true, attributeFilter: ['open'] });
    }
    document.addEventListener('pointerdown', function (e) {
      var t = e.target && e.target.closest && e.target.closest('[aria-controls="DvMobileNav"], [data-dv-open="#DvMobileNav"]');
      if (t) go();
    }, { passive: true, capture: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
